import { Vector3, Quaternion, Matrix4 } from "../../../imports/avm";
import { VisibilityState } from "../InstanceCollectionBase";

// Shared temp for composing local transforms in updateTransformWorld.
const _tempLocalMatrix = new Matrix4();
// Shared temp for reading parent world matrix in updateTransformWorld (external call fallback).
const _tempParentMatrix = new Matrix4();

/**
 * Callback invoked for each node during a {@link Autodesk.Viewing.Scene.Node3D#traverse} walk.
 *
 * @callback NodeTraverseCallback
 * @param {Autodesk.Viewing.Scene.Node3D} node - The node being visited.
 * @returns {void}
 */

/**
 * Basic node type for the scene graph. Use {@link Autodesk.Viewing.Scene.Node3D} as a transform-only
 * parent (e.g. a joint or pivot) and {@link Autodesk.Viewing.Scene.InstanceNode3D} for nodes that
 * represent rendered geometry instances.
 *
 * @alias Autodesk.Viewing.Scene.Node3D
 * @memberOf Autodesk.Viewing.Scene
 * @class
 */
export class Node3D {
  /**
   * Parent node in the scene graph, or `null` for the root.
   * @type {Node3D|null}
   * @alias Autodesk.Viewing.Scene.Node3D#parent
   */
  parent = null;

  /**
   * Direct child nodes.
   * @type {Node3D[]}
   * @alias Autodesk.Viewing.Scene.Node3D#children
   */
  children = [];

  /**
   * Local position relative to the parent.
   * @type {Vector3}
   * @alias Autodesk.Viewing.Scene.Node3D#position
   */
  position = new Vector3(0, 0, 0);

  /**
   * Local rotation relative to the parent.
   * @type {Quaternion}
   * @alias Autodesk.Viewing.Scene.Node3D#quaternion
   */
  quaternion = new Quaternion(0, 0, 0, 1);

  /**
   * Local scale relative to the parent.
   * @type {Vector3}
   * @alias Autodesk.Viewing.Scene.Node3D#scale
   */
  scale = new Vector3(1, 1, 1);

  /** @type {boolean} Flag indicating this node's world matrix needs update. */
  _dirty = true;

  /** @type {boolean} Flag indicating some descendant has _dirty=true (for subtree skipping). */
  _subtreeDirty = true;

  /** @type {InstanceCollectionBase|null} */
  _instanceCollection = null;

  /** @private */
  _detachedState = {
    /** @private @type {VisibilityState} */
    visibilityState: VisibilityState.Visible
  };

  /**
   * Creates a transform-only scene graph node.
   *
   * @alias Autodesk.Viewing.Scene.Node3D
   */
  constructor(skipOwnMatrixWorld = false) {
    if (!skipOwnMatrixWorld) {
      /** @type {Matrix4} Represents the object's transformation matrix in world space. */
      this._matrixWorld = new Matrix4();
    }
  }

  /**
   * Computes the local transform matrix for this node.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#getTransformLocal
   * @param {Matrix4} [target] Matrix to write into.
   * @returns {Matrix4} The target filled with the local matrix, or a new matrix if no target is provided.
   */
  getTransformLocal(target) {
    target = target || new Matrix4();
    target.compose(this.position, this.quaternion, this.scale);
    return target;
  }

  /**
   * Reads the world transform matrix into target.
   * If no target is provided, returns a new matrix.
   * Subclasses (e.g. {@link Autodesk.Viewing.Scene.InstanceNode3D}) override this to read from external storage.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#getTransformWorld
   * @param {Matrix4} [target] Matrix to write into.
   * @returns {Matrix4} The target filled with the world matrix, or a new matrix if no target is provided.
   */
  getTransformWorld(target) {
    return target ? target.copy(this._matrixWorld) : this._matrixWorld.clone();
  }

  /**
   * Sets the visibility state for this node and its descendants.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#setVisibilityState
   * @param {VisibilityState} value
   */
  setVisibilityState(value) {
    if (this._detachedState) {
      this._detachedState.visibilityState = value;
    }
    for (let idx = 0; idx < this.children.length; idx++) {
      this.children[idx].setVisibilityState(value);
    }
  }

  /**
   * @alias Autodesk.Viewing.Scene.Node3D#getVisibilityState
   * @returns {VisibilityState} The visibility state.
   */
  getVisibilityState() {
    return this._detachedState.visibilityState;
  }

  /**
   * Marks this node as needing a world matrix update.
   * Call this after modifying `position`, `quaternion`, or `scale` when using dirty tracking.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#markDirty
   */
  markDirty() {
    if (!this._dirty) {
      this._dirty = true;
      this._subtreeDirty = true;

      // Propagate subtreeDirty up to ancestors (O(depth))
      let ancestor = this.parent;
      while (ancestor && !ancestor._subtreeDirty) {
        ancestor._subtreeDirty = true;
        ancestor = ancestor.parent;
      }
    }
  }

  /**
   * Updates the world transform matrix for this node and its descendants.
   * Computations are performed immediately. Subtrees with no dirty nodes are skipped.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#updateTransformWorld
   * @param {boolean} [force=false] If true, forces update regardless of the dirty flag.
   */
  updateTransformWorld(force = false, _parentWorldMatrix = null) {
    // Check if this node needs updating (dirty or forced)
    const needsUpdate = this._dirty || force;

    if (needsUpdate) {
      this.getTransformLocal(_tempLocalMatrix);

      const parentWorld = _parentWorldMatrix || this.parent?.getTransformWorld?.(_tempParentMatrix);
      if (parentWorld) {
        this._matrixWorld.multiplyMatrices(parentWorld, _tempLocalMatrix);
      } else {
        this._matrixWorld.copy(_tempLocalMatrix);
      }

      // Clear dirty flag after update
      this._dirty = false;
    }

    // Only traverse children if this node was updated OR subtree has dirty nodes
    // When force=false and no subtree is dirty, skip traversing children entirely (O(1) instead of O(n))
    if (needsUpdate || this._subtreeDirty) {
      for (const child of this.children) {
        child.updateTransformWorld(needsUpdate, this._matrixWorld);
      }
      // Clear subtreeDirty after processing children
      this._subtreeDirty = false;
    }
  }

  /**
   * @alias Autodesk.Viewing.Scene.Node3D#isAttached
   * @returns {boolean} True if the node is attached to a model's instance collection, false otherwise.
   */
  isAttached() {
    return this._instanceCollection !== null;
  }

  /**
   * @private
   */
  _detachFromInstanceCollection(instanceIdsToRemove) {
    if (this._instanceCollection) {
      this._detachedState = {
        visibilityState: this.getVisibilityState()
      };

      this._instanceCollection = null;
    }
  }

  /**
   * Removes the given child nodes from this node and from the instance collection.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#remove
   * @param {...Node3D|Node3D[]} children
   * @returns {Node3D} A reference to this node.
   */
  remove(...children) {
    const childArray = children.length === 1 && Array.isArray(children[0]) ? children[0] : children;

    // Used to collect instances to remove from the instance collection in one batch instead of individually. Potentially filled in derived classes like InstanceNode3D.
    const instanceIdsToRemove = [];
    const toRemove = new Set(childArray);

    // Detach children from the instance collection and remove them from the parent
    for (let idx = 0; idx < childArray.length; ++idx) {
      const child = childArray[idx];
      if (child.parent === this) {
        child.traverse((n) => n._detachFromInstanceCollection(instanceIdsToRemove));
        child.parent = null;
      }
    }

    // In-place compaction of the children array
    let writeIdx = 0;
    for (let i = 0; i < this.children.length; i++) {
      if (!toRemove.has(this.children[i])) {
        this.children[writeIdx++] = this.children[i];
      }
    }
    this.children.length = writeIdx;

    this._instanceCollection?.remove(instanceIdsToRemove);

    return this;
  }

  /**
   * Removes this node from its parent and the instance collection.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#removeFromParent
   * @returns {Node3D} A reference to this node.
   */
  removeFromParent() {
    if (this.parent) {
      this.parent.remove(this);
    }
    return this;
  }

  /**
   * Traverses this node and its descendants and calls the callback for each node.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#traverse
   * @param {NodeTraverseCallback} callback - Called once for this node and each descendant.
   */
  traverse(callback) {
    const stack = [this];
    while (stack.length) {
      const n = stack.pop();
      // Add children to stack BEFORE calling callback, as callback may modify n.children
      if (n.children && n.children.length) {
        for (let i = n.children.length - 1; i >= 0; i--) {
          stack.push(n.children[i]);
        }
      }
      callback(n);
    }
  }

  /**
   * @private
   */
  _initialize(instanceCollection, updateMatrices = true) {
    this._instanceCollection = instanceCollection;

    // Update world matrices for this node and all descendants BEFORE
    // child initialization, so children have correct parent transforms
    // when they create instances. Only called on the root node.
    if (updateMatrices) {
      this.updateTransformWorld(true);
    }

    for (const child of this.children) {
      child._initialize(instanceCollection, false);
    }

    return this;
  }


  /**
   * Adds child nodes. If a child already has a parent, it is removed from that parent first.
   * Uninitialized children are attached to this node's instance collection.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#add
   * @param {...Node3D|Node3D[]} children
   * @returns {Node3D} A reference to this node.
   */
  add(...children) {
    const childArray = children.length === 1 && Array.isArray(children[0]) ? children[0] : children;

    for (let idx = 0; idx < childArray.length; ++idx) {
      const child = childArray[idx];
      if (!child || child === this || child.parent === this)
      continue;

      if (child.parent)
      child.parent.remove(child);

      child.parent = this;

      this.children.push(child);
      if (this._instanceCollection)
      child._initialize(this._instanceCollection);
    }

    return this;
  }

  /**
   * Removes all children from this node.
   *
   * @alias Autodesk.Viewing.Scene.Node3D#clear
   * @returns {Node3D} A reference to this node.
   */
  clear() {
    if (this.children.length > 0) {
      const instanceIdsToRemove = [];
      for (let idx = 0; idx < this.children.length; idx++) {
        this.children[idx].traverse((n) => {
          n._detachFromInstanceCollection(instanceIdsToRemove);
        });
        this.children[idx].parent = null;
      }
      this._instanceCollection?.remove(instanceIdsToRemove);
      this.children.length = 0;
    }
    return this;
  }
}